import React, { useEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';

import RunComponent from '../components/Run/LiveAssessmentRunComponent';
import RunReviewComponent from '../components/Run/RunReviewComponent';

// A preview has no host, so nobody drives the questions; the participant
// steps through them on their own.
const PREVIEW_LIVE_DATA = Object.freeze({
  host_id: null,
  current_question_id: null,
});

/**
 * The participant's view of a live assessment, run from an assessment that
 * doesn't exist on the server (the editor's preview). Nothing is loaded,
 * joined or saved: the current question is local state, and responses go to
 * the preview's own handlers, which feed them back in on `assessment`.
 */
export default function LiveAssessmentPreviewRun({
  assessment,
  participant,
  previewRespondToAssessment = noop,
  previewUnrespondToAssessment = noop,
}) {
  const questions = useMemo(
    () => (assessment.question_sets ?? []).flatMap((qs) => qs.questions),
    [assessment]
  );
  const [index, setIndex] = useState(0);

  // Editing can remove questions out from under the preview
  useEffect(() => {
    setIndex((current) => Math.min(current, questions.length));
  }, [questions.length]);

  // There is no welcome step to fill in for a preview
  const previewParticipant = useMemo(
    () => ({ ...participant, completed_welcome: true }),
    [participant]
  );

  if (questions.length === 0) {
    return <p className="text-muted p-4">Add a question to preview it here.</p>;
  }

  if (index >= questions.length) {
    return (
      <RunReviewComponent
        assessment={assessment}
        participant={previewParticipant}
        presences={[]}
        onPreviousQuestion={() => setIndex(questions.length - 1)}
      />
    );
  }

  const handleRespond = (assessmentId, question, answer) =>
    previewRespondToAssessment(assessmentId, question.id, {
      participant_id: previewParticipant.id,
      answer_id: answer?.id ?? null,
      value: answer?.value,
      is_na: answer?.is_na ?? false,
    });

  const handleUnrespond = (assessmentId, question) =>
    previewUnrespondToAssessment(assessmentId, question.id);

  return (
    <RunComponent
      assessment={assessment}
      participant={previewParticipant}
      liveAssessmentData={PREVIEW_LIVE_DATA}
      question={questions[index]}
      presences={[]}
      isSynced={false}
      onRespond={handleRespond}
      onUnrespond={handleUnrespond}
      onNextQuestion={() => setIndex((current) => current + 1)}
      onPreviousQuestion={() => setIndex((current) => Math.max(0, current - 1))}
    />
  );
}
